import "./shared/shared.css";
import { lazy, Suspense } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { useSelector } from "react-redux";
import * as selectors from "./selectors/selectors";
import "./App.css";
import DatadogViewTracker from "./Components/DatadogViewTracker/DatadogViewTracker";
import BillingFrozenGateHost from "./Components/BillingFrozenGate/BillingFrozenGateHost";
import useDatadogUser from "./Hooks/useDatadogUser";
import useOrgOptionAllowlist from "./Hooks/useOrgOptionAllowlist";
import LoadingSpinner from "./Components/LoadingSpinner/loading_spinner";

/**
 * Routes are code-split so a single entry point does not pay for the whole
 * portal. Importing every page eagerly put the dashboard's analytics charts,
 * PDF export and QR scanner into the same bundle as the Express Launch wizard,
 * which is the wizard's dominant load cost. Keep new routes lazy.
 */
const LandingPage = lazy(
  () =>
    import(/* webpackChunkName: "landing" */ "./Pages/Landing/landing_page"),
);
const PdfPageWrapper = lazy(
  () => import(/* webpackChunkName: "pdf" */ "./Pages/PdfPage/pdfPageWrapper"),
);
const MainPage = lazy(
  () =>
    import(/* webpackChunkName: "dashboard" */ "./Pages/Mainpage/main_page"),
);
const Shop = lazy(
  () => import(/* webpackChunkName: "shop" */ "./Components/Shop/shop"),
);
const SocialPoster = lazy(
  () =>
    import(
      /* webpackChunkName: "social-poster" */ "./Components/SocialPoster/socialPoster"
    ),
);
const SocialVoucherPoster = lazy(
  () =>
    import(
      /* webpackChunkName: "social-voucher" */ "./Components/SocialPoster-voucher/socialPoster-voucher"
    ),
);
const Email_Verification_Page = lazy(
  () =>
    import(
      /* webpackChunkName: "email-verification" */ "./Pages/EmailVerification/email_verification"
    ),
);
const LoginImpersonatePage = lazy(
  () =>
    import(
      /* webpackChunkName: "impersonate" */ "./Pages/LoginImpersonate/login_impersonate_page"
    ),
);
const LoginResellerPage = lazy(
  () =>
    import(
      /* webpackChunkName: "reseller" */ "./Pages/LoginReseller/login_reseller_page"
    ),
);
const Approval = lazy(
  () =>
    import(
      /* webpackChunkName: "payment" */ "./Pages/PaymentApproval/approval"
    ),
);
const OnBoardingForm = lazy(
  () =>
    import(
      /* webpackChunkName: "onboarding-form" */ "./Pages/OnBoardingForm/OnBoardingForm"
    ),
);
const WizardMark2ExpressPage = lazy(
  () =>
    import(
      /* webpackChunkName: "express-launch" */ "./Pages/WizardMark2Express/WizardMark2ExpressRevampPage"
    ),
);
const StampRedeem = lazy(
  () =>
    import(
      /* webpackChunkName: "stamp-redeem" */ "./Pages/StampRedeem/stamp_redeem"
    ),
);
const VirtualCardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "virtual-card" */ "./Pages/VirtualCard/virtual_card_page"
    ),
);
const ShopLoginPage = lazy(
  () =>
    import(
      /* webpackChunkName: "shop-login" */ "./Pages/ShopLogin/ShopLoginPage"
    ),
);

function WizardSection() {
  return <Outlet />;
}

function AppContent() {
  const isAuthenticated = useSelector(selectors.getIsAuthenticated);

  // Automatically sync user data with Datadog RUM
  useDatadogUser();
  useOrgOptionAllowlist();

  return (
    <>
      <DatadogViewTracker />
      <BillingFrozenGateHost />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/wizard" element={<WizardSection />}>
            <Route path="ExpressLaunch" element={<WizardMark2ExpressPage />} />
            <Route
              path=":wizardname/*"
              element={<Navigate to="/wizard/ExpressLaunch" replace />}
            />
          </Route>
          <Route path="/fandbhongkong" element={<OnBoardingForm />} />
          <Route path="/" element={<LandingPage />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/pdf" element={<PdfPageWrapper />} />
          <Route path="/dashboard/:page?/:id?" element={<MainPage />} />
          <Route path="/impersonate" element={<LoginImpersonatePage />} />
          <Route
            path="/referrer/:referralCode?"
            element={<LoginResellerPage />}
          />
          <Route path="/social" element={<SocialPoster />} />
          <Route path="/social-voucher" element={<SocialVoucherPoster />} />
          <Route path="/verify/:token?" element={<Email_Verification_Page />} />
          <Route path="/payment" element={<Approval />} />
          <Route path="/stamp-redeem" element={<StampRedeem />} />
          <Route path="/virtual-card" element={<VirtualCardPage />} />
          {/* Public: reached by scanning the staff login QR poster with a
              phone's plain camera, before the meed Business app is even
              installed. Must never require auth or redirect to login. */}
          <Route path="/shop-login" element={<ShopLoginPage />} />
          <Route
            path="*"
            element={
              <Navigate to={isAuthenticated ? "/dashboard" : "/"} replace />
            }
          />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
